import React, { useCallback, useEffect, useMemo, useState } from "react";
import { parse as queryStringParse, stringify as queryStringStringify } from "query-string";
import _ from "lodash";
import {
  getFromSessionStorage,
  removeFromSessionStorage,
  saveToSessionStorage,
} from "../../utils/misc";
import media from "../../utils/media";
import constants from "../../utils/constants";
import { useSelector } from "react-redux";
import { selectors as appSelectors } from "../../containers/App";
import { selectors as reservationSelectors } from "../../containers/ReservationManager";
import { requestBookingEngineData } from "../../containers/App/reducer";
import styled from "styled-components";
import { white } from "../../theme/variables";
import { defineMessages, FormattedMessage } from "react-intl";
import { BuyIcon, Close } from "../../theme/assets";
import { useDispatch } from "react-redux";
import { extrasToShowSelector } from "../../containers/Checkout/selectors";

const selectors = { ...appSelectors, ...reservationSelectors };

const msg = defineMessages({
  select: {
    id: "assisted.select",
    description: "assisted.select",
    defaultMessage: "Select",
  },
  rates: {
    id: "assisted.rates",
    description: "assisted.rates",
    defaultMessage: "See rates",
  },
  exit: {
    id: "assisted.exit",
    description: "assisted.exit",
    defaultMessage: "Continue without assistance",
  },
  change: {
    id: "assisted.change",
    description: "assisted.change",
    defaultMessage: "Change guests",
  },
  unavailable: {
    id: "assisted.unavailable",
    description: "assisted.unavailable",
    defaultMessage: "Not available",
  },
  success: {
    id: "assisted.success",
    description: "assisted.success",
    defaultMessage: "Success",
  },
  checkoutMessage: {
    id: "assisted.checkoutMessage",
    description: "assisted.checkoutMessage",
    defaultMessage: "Add services and proceed to checkout",
  },
  addServices: {
    id: "assisted.addServices",
    description: "assisted.addServices",
    defaultMessage: "Add services",
  },
  checkout: {
    id: "assisted.checkout",
    description: "assisted.checkout",
    defaultMessage: "Proceed to checkout",
  },
  confirm: {
    id: "assisted.confirm",
    description: "assisted.confirm",
    defaultMessage: "Confirm",
  },
  adult: {
    id: "roomSelection.occupant.adult",
    description: "adult",
    defaultMessage: "adult",
  },
  adults: {
    id: "roomSelection.occupant.adults",
    description: "adults",
    defaultMessage: "adults",
  },
  child: {
    id: "roomSelection.occupant.child",
    description: "child",
    defaultMessage: "child",
  },
  children: {
    id: "roomSelection.occupant.children",
    description: "children",
    defaultMessage: "children",
  },
  baby: {
    id: "roomSelection.occupant.baby",
    description: "baby",
    defaultMessage: "baby",
  },
  babies: {
    id: "roomSelection.occupant.babies",
    description: "babies",
    defaultMessage: "babies",
  },
  senior: {
    id: "roomSelection.occupant.senior",
    description: "senior",
    defaultMessage: "senior",
  },
  seniors: {
    id: "roomSelection.occupant.seniors",
    description: "seniors",
    defaultMessage: "seniors",
  },
  teenager: {
    id: "roomSelection.occupant.teenager",
    description: "teenager",
    defaultMessage: "teenager",
  },
  teenagers: {
    id: "roomSelection.occupant.teenagers",
    description: "teenagers",
    defaultMessage: "teenagers",
  },
  occupants: {
    id: "checkout.reservation_summary.occupants",
    description: "teenagers",
    defaultMessage: "teenagers",
  },
});

const StyledOverlay = styled.div`
  background: rgba(0, 0, 0, 0.7);
  color: ${white};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  position: fixed;
`;

const StyledHeader = styled.div`
  height: 40px;
  background-color: ${props => props.backgroundColor};
  color: ${props => props.oppositeColor};
  align-items: center;
  justify-content: center;
  display: flex;
  font-size: 14px;
  font-weight: 700;
  & > *:nth-child(2) {
    position: absolute;
    right: 10px;
    font-size: 12px;
    font-weight: 400;
    text-decoration: underline;
    cursor: pointer;
  }
`;

const StyledBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: column;
  height: 100%;
  color: ${white};
  padding: 20px;
`;

const Container = styled.span`
  font-size: 25px;
  text-align: center;
  ${media.up.md`
    font-size: 40px;
  `};
  margin: 0 0 20px 0;
  padding: 0;
`;

const Step = styled(Container)`
  margin-bottom: 0;
  font-weight: 400;
`;

const StepFontWeight = styled.div`
  font-weight: 400;
`;

const Success = styled(Container)``;

const Checkout = styled(Container)``;

const Guests = styled(Container)`
  font-weight: 700;
  text-align: center;
  & > * {
    display: inline-block;
  }
`;

const Button = styled.div`
  border: 1px solid ${white};
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  border-radius: 5px;
  display: flex;
  padding: 10px 30px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  text-align: center;
`;

const ButtonWrapper = styled.div`
  display: inline-block;
  margin: ${props => (props.checkout ? "15px 0 40px" : "10px 0 40px")};
`;

const Exit = styled.div`
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
  user-select: none;
`;

const StyledClose = styled(Close)`
  margin-left: 5px;
  width: 15px;
  height: 15px;
  display: inline-block !important;
`;

const HeaderExitMessage = styled.span`
  display: none;
  text-decoration: underline;
  ${media.up.md`
    display:inline-block;
  `};
`;

const StyledBuyIcon = styled(BuyIcon)`
  display: inline-block !important;
  width: 25px;
  ${media.up.md`
      width: 45px;
  `};
`;

function removeKeysFromSessionStorage() {
  removeFromSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE);
  removeFromSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE_INDEX);
  removeFromSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE_DATE_IN);
  removeFromSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE_DATE_OUT);
  removeFromSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE_AGES);
}

function parseStayParams(stay, guests) {
  const transformedStay = [];
  const guestAges = [];
  if (Array.isArray(stay)) {
    stay.forEach(it => {
      if (typeof it === "string" && it.includes("[") && it.includes("]")) {
        it = it.substring(0, it.length - 1);
        const parsed = it.split("[");
        transformedStay.push(
          guests.reduce(
            (accum, curr, idx) => ({
              ...accum,
              [guests[idx]]: idx === 0 ? Number(parsed[0]) : 0,
            }),
            {}
          )
        );
        if (parsed[1]) {
          guestAges.push(parsed[1].split(",").map(it => Number(it)));
        } else {
          guestAges.push([]);
        }
      } else {
        const parsedGuests =
          typeof it === "string" &&
          it.split(",").reduce(
            (accum, curr, idx) => ({
              ...accum,
              [guests[idx]]: Number.parseInt(curr),
            }),
            {}
          );

        transformedStay.push(parsedGuests);
        guestAges.push([]);
      }
    });
  }
  return {
    transformedStay,
    guestAges,
  };
}

function initializeStorage({
  getFromSessionStorage,
  setSessionStorageReady,
  setStayQueue,
  saveToSessionStorage,
  location,
  setStayQueueParams,
  setStayAges,
}) {
  const queue = getFromSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE);
  const ages = getFromSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE_AGES);
  const queryParams = (location && location.search && queryStringParse(location.search)) || {};
  if (
    typeof getFromSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE_INDEX) === "number" &&
    queue
  ) {
    //Initialization complete as session storage already exists
    setSessionStorageReady(true);
    setStayQueue(queue);
    setStayAges(ages);
  } else {
    let stay = Array.isArray(queryParams.stay) ? queryParams.stay : [queryParams.stay];
    setStayQueueParams(stay);
    const guests = ["adults", "children", "seniors", "teenagers", "babies"];
    const { transformedStay, guestAges } = parseStayParams(stay, guests);
    // The params should have the following format: adults,children,seniors,teenagers,babies.
    // Where each one of this is an integer indicating the number of people that are to be in that same room.
    // If a parameter is to be ignored, its value must be 0.
    // If any stay param does not comply with the above syntax, assisted mode won’t be enabled.

    if (
      Array.isArray(transformedStay) &&
      transformedStay.length &&
      transformedStay.every(it => Object.values(it).length === guests.length) &&
      transformedStay.every(it => Object.values(it).every(n => Number.isInteger(n) && n >= 0))
    ) {
      saveToSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE, transformedStay);
      saveToSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE_INDEX, 0);
      saveToSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE_DATE_IN, queryParams.datein);
      saveToSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE_DATE_OUT, queryParams.dateout);
      saveToSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE_AGES, guestAges);
      setSessionStorageReady(true);
      setStayQueue(transformedStay);
      setStayAges(guestAges);
    } else {
      console.warn(
        `Some of the 'stay' query parameters contain errors.
         This param should have the following format: adults,children,seniors,teenagers,babies.
         Where each one of this is an integer indicating the number of people that are to be in that same room.
         If a parameter is to be ignored, its value must be 0.`
      );
    }
  }
}

export default function AssistedMode({ location, history, params, locale, stayQueueIndex }) {
  const [sessionStorageReady, setSessionStorageReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [stayQueue, setStayQueue] = useState([]);
  const [stayAges, setStayAges] = useState([]);
  const isPropertyAvailable = useSelector(selectors.isPropertyAvailableSelector);
  const customStyles = useSelector(selectors.customStylesSelector);
  const reservationCreated = useSelector(selectors.reservationCreatedSelector);
  const extrasToShow = useSelector(extrasToShowSelector);
  const [stayQueueParams, setStayQueueParams] = useState("");
  const dispatch = useDispatch();
  const queryParams = useMemo(
    () => (location && location.search && queryStringParse(location.search)) || {},
    [location]
  );

  function resetStayQueue() {
    removeKeysFromSessionStorage();
    setSessionStorageReady(false);
    setOpen(false);
  }

  useEffect(() => {
    const stay = Array.isArray(queryParams.stay) ? queryParams.stay : [queryParams.stay];
    if (
      (!stayQueue.length && !stayQueueParams) ||
      (queryParams && queryParams.stay && !_.isEqual(stayQueueParams, stay))
    ) {
      resetStayQueue();
      initializeStorage({
        getFromSessionStorage,
        setSessionStorageReady,
        setStayQueue,
        saveToSessionStorage,
        location,
        setStayQueueParams,
        setStayAges,
      });
    }
  }, [stayQueueParams, queryParams]);

  useEffect(
    function clearSessionStorageAfterReservationIsMade() {
      if (reservationCreated) {
        resetStayQueue();
      }
    },
    [reservationCreated]
  );

  useEffect(
    function removeConflictingQueryParams() {
      const queryParams = (location && location.search && queryStringParse(location.search)) || {};
      if (sessionStorageReady) {
        const newQueryParam = { ...queryParams };

        const keywords = [
          "seniors",
          "adults",
          "adultos",
          "teenagers",
          "children",
          "ninos",
          "babies",
          "bebes",
          "personas",
          "guestAges",
          "seniorSelectorRange",
          "adultSelectorRange",
          "teenagerSelectorRange",
          "childSelectorRange",
          "babySelectorRange",
        ];

        keywords.forEach(keyword => {
          if (newQueryParam[keyword]) {
            delete newQueryParam[keyword];
          }
        });

        const newQueryParamString = queryStringStringify(newQueryParam, {
          encode: false,
        });

        history.replace(`${history.location.pathname}?${newQueryParamString}`);
      }
    },
    [sessionStorageReady]
  );
  useEffect(
    function fetchAriWithQueueData() {
      async function fetchARI() {
        if (
          sessionStorageReady &&
          stayQueue.length &&
          stayAges &&
          stayQueueIndex <= stayQueue.length - 1
        ) {
          const item = stayQueue[stayQueueIndex];
          const guestAges = stayAges[stayQueueIndex];
          const { adults, children, seniors, teenagers, babies } = item || {};
          dispatch(
            requestBookingEngineData({
              hotels: params.hotel,
              hotelTicker: params.hotelTicker,
              datein: getFromSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE_DATE_IN),
              dateout: getFromSessionStorage(constants.LOCAL_STORAGE_KEYS.STAY_QUEUE_DATE_OUT),
              seniors,
              adults,
              teenagers,
              children,
              babies,
              guestAges,
            })
          );
        }
      }

      fetchARI();
    },
    [stayQueueIndex, stayQueue, sessionStorageReady]
  );

  useEffect(
    function openOverlayIfConditionsMet() {
      if (sessionStorageReady && stayQueueIndex <= stayQueue.length && stayQueue.length > 1) {
        setOpen(true);
      }
    },
    [stayQueueIndex, sessionStorageReady]
  );

  useEffect(
    function navigation() {
      if (sessionStorageReady) {
        if (stayQueueIndex >= stayQueue.length) {
          history.push(`${constants.STEP_PATHS.STEP_2}`);
        } else {
          history.push(`${constants.STEP_PATHS.STEP_1}`);
        }
      }
    },
    [sessionStorageReady, stayQueueIndex]
  );

  const handleExit = useCallback(() => {
    removeKeysFromSessionStorage();
    setSessionStorageReady(false);
    setOpen(false);
  }, []);

  const currentQueue = sessionStorageReady && stayQueue && stayQueue[stayQueueIndex];
  const currentAges = sessionStorageReady && stayAges && stayAges[stayQueueIndex];
  const { adults, children, seniors, teenagers, babies } = currentQueue || {};
  const firstReservation = Number(stayQueueIndex) === 0;
  const checkout = sessionStorageReady && stayQueueIndex > stayQueue.length - 1;
  const step = sessionStorageReady && `(${Number(stayQueueIndex) + 1}/${stayQueue.length})`;

  const handleClose = useCallback(() => {
    if (checkout) {
      setTimeout(() => {
        handleExit();
      }, 5000);
    }
    setOpen(false);
  }, [checkout]);

  return (
    <>
      {sessionStorageReady && open && (
        <StyledOverlay>
          <StyledHeader
            backgroundColor={`#${customStyles.get("bookingFormButtonBackgroundColor")}`}
            oppositeColor={`#${customStyles.get("bookingFormButtonOppositeColor")}`}>
            {checkout ? (
              <div>
                <FormattedMessage {...msg.confirm} />
              </div>
            ) : (
              <>
                <div>
                  <FormattedMessage {...msg.select} /> <span>{step}</span>
                </div>
                <div onClick={handleExit}>
                  <HeaderExitMessage>
                    <FormattedMessage {...msg.exit} />
                  </HeaderExitMessage>
                  <StyledClose />
                </div>
              </>
            )}
          </StyledHeader>
          <StyledBody>
            {!firstReservation && isPropertyAvailable && (
              <Success>
                <FormattedMessage {...msg.success} /> <StyledBuyIcon />
              </Success>
            )}
            {!checkout ? (
              <>
                {!isPropertyAvailable && (
                  <Step>
                    <FormattedMessage {...msg.unavailable} />
                  </Step>
                )}
                <Guests>
                  {!firstReservation && isPropertyAvailable && (
                    <Step>
                      <FormattedMessage {...msg.checkout} />
                      :&nbsp;
                    </Step>
                  )}
                  {currentAges.length ? (
                    <div>
                      {adults + currentAges.length} &nbsp;
                      <FormattedMessage {...msg.occupants} />
                    </div>
                  ) : (
                    <>
                      {!!adults && (
                        <div>
                          {adults}{" "}
                          {adults > 1 ? (
                            <FormattedMessage {...msg.adults} />
                          ) : (
                            <FormattedMessage {...msg.adult} />
                          )}{" "}
                        </div>
                      )}
                      {!!children && (
                        <div>
                          &nbsp;+ {children}{" "}
                          {children > 1 ? (
                            <FormattedMessage {...msg.children} />
                          ) : (
                            <FormattedMessage {...msg.child} />
                          )}{" "}
                        </div>
                      )}
                      {!!seniors && (
                        <div>
                          &nbsp;+ {seniors}{" "}
                          {seniors > 1 ? (
                            <FormattedMessage {...msg.seniors} />
                          ) : (
                            <FormattedMessage {...msg.senior} />
                          )}{" "}
                        </div>
                      )}
                      {!!teenagers && (
                        <div>
                          &nbsp;+ {teenagers}{" "}
                          {teenagers > 1 ? (
                            <FormattedMessage {...msg.teenagers} />
                          ) : (
                            <FormattedMessage {...msg.teenager} />
                          )}{" "}
                        </div>
                      )}
                      {!!babies && (
                        <div>
                          &nbsp;+ {babies}{" "}
                          {babies > 1 ? (
                            <FormattedMessage {...msg.babies} />
                          ) : (
                            <FormattedMessage {...msg.baby} />
                          )}{" "}
                        </div>
                      )}
                    </>
                  )}
                  <StepFontWeight> &nbsp;{step}</StepFontWeight>
                </Guests>
              </>
            ) : checkout && extrasToShow ? (
              <Checkout>
                <FormattedMessage {...msg.checkoutMessage} />
              </Checkout>
            ) : null}
            <ButtonWrapper checkout={checkout} onClick={handleClose}>
              <Button>
                <div>
                  {checkout ? (
                    <div>
                      <FormattedMessage {...msg.checkout} />
                    </div>
                  ) : !isPropertyAvailable ? (
                    <FormattedMessage {...msg.change} />
                  ) : (
                    <FormattedMessage {...msg.rates} />
                  )}
                </div>
              </Button>
            </ButtonWrapper>
            {!checkout && (
              <Exit onClick={handleExit}>
                <FormattedMessage {...msg.exit} />
              </Exit>
            )}
          </StyledBody>
        </StyledOverlay>
      )}
    </>
  );
}
